import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { LocalUser } from '../../domain/models/local-user.model';
import { LocalUserCreateInput } from '../../domain/models/local-user-create-input.model';
import { LocalUserUpdateInput } from '../../domain/models/local-user-update-input.model';
import {
  CreateDeveloperClientInput, ILocalUsersRepository, StaffRoster,
} from '../../domain/repositories/local-users.repository';
import { LocalUsersRemoteDatasource } from '../datasources/remote/local-users.remote-datasource';
import { RolesRemoteDatasource } from '../datasources/remote/roles.remote-datasource';
import { LocalUserMapper } from '../mappers/local-user.mapper';
import { UnlinkedVeterinaryUserMapper } from '../mappers/unlinked-veterinary-user.mapper';

const DEVELOPER_ROLE_NAME = 'Developer';

@Injectable()
export class LocalUsersRepositoryImpl implements ILocalUsersRepository {
  private readonly datasource = inject(LocalUsersRemoteDatasource);
  private readonly rolesDatasource = inject(RolesRemoteDatasource);
  private readonly mapper = inject(LocalUserMapper);
  private readonly unlinkedVeterinaryUserMapper = inject(UnlinkedVeterinaryUserMapper);

  public list(organizationId: number): Observable<readonly LocalUser[]> {
    return this.datasource.list(organizationId).pipe(
      map((dtos) => dtos
        .map((dto) => this.mapper.toLocalUser(dto))
        .filter((user): user is LocalUser => user !== null)),
    );
  }

  public getRoster(organizationId: number): Observable<StaffRoster> {
    return this.datasource.getRoster(organizationId).pipe(
      map((dto) => ({
        items: dto.Linked
          .map((linked) => this.mapper.toLocalUser(linked))
          .filter((user): user is LocalUser => user !== null),
        unlinkedVeterinaryUsers: dto.Unlinked.map((unlinked) => this.unlinkedVeterinaryUserMapper.toModel(unlinked)),
      })),
    );
  }

  public create(organizationId: number, input: LocalUserCreateInput): Observable<LocalUser> {
    const payload = this.mapper.toCreatePayload(input);
    return this.datasource.create(organizationId, payload).pipe(
      map((dto) => this.toLocalUserOrThrow(dto)),
    );
  }

  public update(organizationId: number, id: number, input: LocalUserUpdateInput): Observable<LocalUser> {
    const payload = this.mapper.toUpdatePayload(input);
    return this.datasource.update(organizationId, id, payload).pipe(
      map((dto) => this.toLocalUserOrThrow(dto)),
    );
  }

  public deactivate(organizationId: number, id: number): Observable<void> {
    return this.datasource.deactivate(organizationId, id);
  }

  public deletePermanently(organizationId: number, id: number): Observable<void> {
    return this.datasource.deletePermanently(organizationId, id);
  }

  public resendInvitation(organizationId: number, id: number): Observable<void> {
    return this.datasource.resendInvitation(organizationId, id);
  }

  public createDeveloperClient(organizationId: number, input: CreateDeveloperClientInput): Observable<void> {
    return this.rolesDatasource.list().pipe(
      switchMap((roles) => {
        const developerRole = roles.find((role) => role.Name === DEVELOPER_ROLE_NAME);
        if (!developerRole) {
          throw new Error(`"${DEVELOPER_ROLE_NAME}" role not found`);
        }

        return this.datasource.create(organizationId, {
          Username: input.username,
          Password: input.password,
          FirstName: input.firstName,
          LastName: input.lastName,
          RoleId: developerRole.Id,
          DepartmentId: input.departmentId,
        });
      }),
      map(() => undefined),
    );
  }

  private toLocalUserOrThrow(dto: Parameters<LocalUserMapper['toLocalUser']>[0]): LocalUser {
    const user = this.mapper.toLocalUser(dto);
    if (!user) {
      throw new Error(`Unexpected non-managed role in local user response: ${dto.Role}`);
    }
    return user;
  }
}
